import React, { useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { useDataQuarterFilterContext } from '../DataQuarterFilterContext';
import { DataQuarterFilterFormValues } from '../types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 220px;
  border-right: 1px solid var(--line-gray);
`;

const Quarters = styled.div`
  overflow-y: auto;
`;

const QuarterItem = styled.div<{ selected: boolean }>`
  padding: 8px 10px;
  font-size: 14px;
  line-height: 19px;
  cursor: pointer;
  border-bottom: 1px solid var(--line-gray);
  background-color: ${(props) => (props.selected ? 'var(--button-blue)' : 'white')};
  color: ${(props) => (props.selected ? 'white' : 'var(--text-primary)')};
`;

const ShowOlderRow = styled.div`
  padding: 10px 10px;
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
`;

const VISIBLE_COUNT = 4;

const QuarterSelection = () => {
  const { quarters, form } = useDataQuarterFilterContext();
  const selectedQuarterSlug = Form.useWatch('selectedQuarterSlug', form) ?? null;
  const includePriorCompetitors = Form.useWatch('includePriorCompetitors', form) ?? false;
  const [showOlder, setShowOlder] = useState(false);

  const visibleQuarterSlugs = useMemo(
    () => quarters.slice(0, VISIBLE_COUNT).map((quarter) => quarter.slug),
    [quarters]
  );

  useEffect(() => {
    if (showOlder) {
      return;
    }

    if (!selectedQuarterSlug) {
      return;
    }

    if (!visibleQuarterSlugs.includes(selectedQuarterSlug)) {
      setShowOlder(true);
    }
  }, [selectedQuarterSlug, visibleQuarterSlugs, showOlder]);

  const availableQuarters = useMemo(() => {
    return showOlder ? quarters : quarters.slice(0, VISIBLE_COUNT);
  }, [showOlder, quarters]);

  return (
    <Container>
      <Quarters>
        {availableQuarters.map((quarter, index) => (
          <QuarterItem
            key={quarter.slug}
            selected={selectedQuarterSlug === quarter.slug}
            onClick={() => {
              const mostRecentSlug = quarters[0]?.slug ?? null;
              const isLeavingMostRecent =
                Boolean(mostRecentSlug) &&
                selectedQuarterSlug === mostRecentSlug &&
                quarter.slug !== mostRecentSlug &&
                includePriorCompetitors;
              const isReturningToMostRecent =
                Boolean(mostRecentSlug) &&
                selectedQuarterSlug !== mostRecentSlug &&
                quarter.slug === mostRecentSlug &&
                !includePriorCompetitors;

              const updates: Partial<DataQuarterFilterFormValues> = {
                selectedQuarterSlug: quarter.slug,
              };

              if (isLeavingMostRecent) {
                updates.includePriorCompetitors = false;
              } else if (isReturningToMostRecent) {
                updates.includePriorCompetitors = true;
              }

              form.setFieldsValue(updates);
            }}
          >
            {index === 0 ? 'Most recent' : quarter.label}
          </QuarterItem>
        ))}
      </Quarters>
      {quarters.length > VISIBLE_COUNT && !showOlder && (
        <ShowOlderRow onClick={() => setShowOlder(true)}>Show older data</ShowOlderRow>
      )}
    </Container>
  );
};

export default QuarterSelection;
